import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Row, Col, Form } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment';

import { requestList as request } from 'api/sfms/rawMaterial/nutrition';
import CheckPlant from 'components/common-sfims/CheckPlant';
import MonthPicker from 'components/common-sfims/form/MonthPicker';
import ListInContent from 'components/common-sfims/ListInContent';
import columns from 'components/pages/sfms/rawMaterial/nutrition/columns';
import Export from 'components/pages/sfms/rawMaterial/nutrition/Export';
import Upload from 'components/pages/sfms/rawMaterial/nutrition/Upload';
import { datas2ExcelJSON, utc2tz } from 'function/convert';
import { downloadExcelByJSON } from 'function/file';
import { getText } from 'function/language';
import useCheckPlantState from 'global/checkPlantState';
import usePlantState from 'global/plantState';
import useListScroll from 'hooks/useListScroll';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import { BEST_MIX_STATUS } from 'types/type';
import { comma, number as formatNumber } from 'utils/format';
import format from 'utils/string/format';

/**
 * 배합비 작성 정보 관리 > 원료 영양 성분 - 목록
 */
const List = () => {
  /* hook */
  const { end, setEnd, total, setTotal } = useListScroll();

  /* router */
  const { state } = useLocation();

  /* recoil */
  const { checkPlant } = useCheckPlantState();
  const {
    plant: { alls: plants }
  } = usePlantState();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // BestMix 입력 확인 모달 표시 여부
  const [isShowExport, setIsShowExport] = useState(false);
  // 업로드 모달 표시 여부
  const [isShowUpload, setIsShowUpload] = useState(false);
  // 메시지
  const [message, setMessage] = useState({});
  // 원료 영양 성분 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer({
    factoryCodeSeqList: checkPlant,
    regYm: moment()
      .add(moment().date() >= 22 ? 1 : 0, 'months')
      .format('YYYYMM'),
    ...state
  });
  // 원료 코드 검색어
  const [rawMtrlCd, setRawMtrlCd] = useState(state?.rawMtrlCd ?? '');
  // 선택한 월
  const [regYm, setRegYm] = useState(state?.regYm ?? params.regYm);

  /* watch */
  useEffect(() => {
    if (plants.length > 0) {
      requestList();
    }
  }, [plants, params]);

  /* method */
  // 원료 영양 성분 목록 조회 요청
  const requestList = async () => {
    setEnd(20);
    setTotal(0);
    setDatas([]);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setTotal(totalCount);
      setDatas(
        list.map((value = {}, index) => ({
          ...value,
          rowNum: totalCount - index,
          pltCode: plants.find(({ seq } = {}) => seq === value.factoryCodeSeq)
            ?.pltCode,
          regDt: utc2tz(value.regDt),
          transDt: utc2tz(value.transDt)
        }))
      );
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  /* event */
  // 엑셀 다운로드 클릭 시
  const onDownloadExcelClick = () => {
    downloadExcelByJSON(
      datas2ExcelJSON(datas, columns),
      [getText('rawMaterialNutrition'), moment(params.regYm).format('YYMM')]
        .join('_')
        .concat('.xlsx')
    );
  };
  // 검색 클릭 시
  const onSearchClick = event => {
    event.preventDefault();

    setParams({
      factoryCodeSeqList: checkPlant,
      regYm,
      rawMtrlCd
    });
  };

  /* view */
  return (
    <>
      <ListInContent
        columns={Object.entries(columns).map(
          ([key, { title } = {}]) => title ?? key
        )}
        message={message}
        showLoading={isLoading}
      >
        <Fragment key="headerButton">
          <Button
            size="sm"
            className="ms-2"
            onClick={() => setIsShowUpload(true)}
          >
            {getText('uploadExcel')}
          </Button>
          <Button
            size="sm"
            className="ms-2"
            onClick={() => setIsShowExport(true)}
          >
            {getText('inputBestMix')}
          </Button>
        </Fragment>
        <Fragment key="search">
          <CheckPlant value={params.factoryCodeSeqList} />
          <Form onSubmit={onSearchClick}>
            <Row className="ms-0 me-0 mt-3">
              <Col sm={1} className="ps-1 pe-1">
                <MonthPicker value={regYm} onUpdate={setRegYm} />
              </Col>
              <Col sm={2} className="ps-1 pe-1">
                <Form.Control
                  size="sm"
                  type="text"
                  value={rawMtrlCd}
                  autoFocus
                  placeholder={getText('rwmtrCd')}
                  onChange={({ target: { value } }) => setRawMtrlCd(value)}
                />
              </Col>
              <Col
                sm={3}
                className="ps-1 pe-1 d-flex flex-row align-items-center"
              >
                <Button disabled={isLoading} size="sm" type="submit">
                  {getText('search')}
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  {format(getText('total'), { total: comma(total) })}
                </p>
                {datas.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ms-2"
                    onClick={onDownloadExcelClick}
                  >
                    {getText('downloadExcel')}
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Fragment>
        <Fragment key="datas">
          {datas.slice(0, end).map((value = {}) => (
            <tr key={`datas${value.seq}`}>
              {Object.entries(columns).map(([key, { type, format } = {}]) => (
                <td
                  key={`datas${value.seq}.${key}`}
                  className={classNames({ 'text-end': type === 'number' })}
                >
                  {key === 'bestmixStatus' ? (
                    value.bestmixStatus ? (
                      <span
                        className={classNames({
                          'text-warning':
                            value.bestmixStatus === BEST_MIX_STATUS.UPLOAD,
                          'text-primary':
                            value.bestmixStatus ===
                            BEST_MIX_STATUS.BESTMIX_TRANS
                        })}
                      >
                        {value.bestmixStatus}
                      </span>
                    ) : (
                      value.bestmixStatus
                    )
                  ) : type === 'number' ? (
                    formatNumber(value[key], format)
                  ) : (
                    value[key]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </Fragment>
      </ListInContent>
      <Upload
        show={isShowUpload}
        onHide={() => setIsShowUpload(false)}
        onUpload={requestList}
      />
      <Export
        show={isShowExport}
        onHide={() => setIsShowExport(false)}
        onExport={requestList}
      />
    </>
  );
};

export default List;
